import _ from 'underscore'
import DashboardLoader from '../dashboard/loader'
import ErrorLoader from '../errors/loader'
import EventLoader from '../monitor/events/loader'
import MonitorLoader from '../monitor/loader'
import SnapshotLoader from '../monitor/snapshot/loader'
import SnapshotModel from '../monitor/snapshot/snapshotModel'
import OperationLoader from '../operation/loader'
import ProfileListLoader from '../profile-list/loader'
import TraceLoader from '../trace/loader'
import VisibilityChange from '../visibilityChange'
import Api from './api'
import Range from './timeRange'

/**
 * This class defines which content is to be loaded and written into the global
 * store for which route.
 *
 * For each route there should be a set of load operations which will then
 * populate the global store. The route parameters are parsed from the current
 * path and usually determine the exact entities to load. Thus the route
 * parameters are usually passed to the load() method.
 *
 * If you require re-mapping of those parameters this should not be done in
 * here, but in the dedicated loader class.
 */
const Loader = function(store, router, api) {
    this.store = store
    this.router = router
    this.api = api || new Api(router, store)
    this.visibility = new VisibilityChange()
    this.visibility.registerCallBack(this.api.pauseRequests, this.api.resumeRequests)

    this.operationLoader = new OperationLoader(store, this.api)
    this.traceLoader = new TraceLoader(store, this.api)
    this.snapshotLoader = new SnapshotLoader(store, this.api)
    this.eventLoader = new EventLoader(store, this.api)
    this.monitorLoader = new MonitorLoader(store, this.api)
    this.profileListLoader = new ProfileListLoader(store, this.api)
    this.dashboardLoader = new DashboardLoader(store, this.api)
    this.errorLoader = new ErrorLoader(store, this.api)

    this.loadContentForPath = function(pathname: string, query = {}, previousPathname: string | null = null) {
        const route = router.match(pathname)
        route.query = query
        route.urlParameters = _.extend({}, route.parameters)
        route.parameters = _.extend({}, route.urlParameters, route.query)

        const routeChanged = !!(previousPathname && previousPathname !== pathname)

        this.api.clearContinuousRequests()
        this.store.dispatch({ type: 'TIDEWAYS_ROUTE', route, routeChanged })

        // @ts-ignore
        if (!PRODUCTION) {
            console.groupCollapsed('%cSwitched Route: %c%s', 'color: gray', 'color: darkblue', route.route)

            console.info(route)

            console.groupEnd()
        }

        let routeToLoadFor = route.route

        if (route.route === 'XhprofOrganizationBundle.Application.show' && this.store.getState().tidewaysContext?.isProfilingSpace()) {
            routeToLoadFor = 'XhprofProfilerBundle.Profile.list'
        }

        switch (routeToLoadFor) {
            case 'dashboard':
                this.dashboardLoader.load(route.parameters)
                break

            case 'XhprofProfilerBundle.Transaction.show':
            case 'XhprofProfilerBundle.OperationSnapshot.show': {
                this.monitorLoader.loadBaseServiceHistoryRange(route.parameters)
                this.operationLoader.load(route.parameters)
                this.operationLoader.loadGraph(route.parameters)
                this.operationLoader.loadHistogram(route.parameters)
                this.operationLoader.loadTaggedVariants(route.parameters)

                const range = route.parameters.end ? SnapshotModel.fromParameters(route.parameters) : Range.fromParameters(route.parameters)
                this.profileListLoader.loadOperation({ ...route.parameters, ...range.toMinMaxDate() })

                break
            }
            case 'XhprofProfilerBundle.Profile.show':
            case 'XhprofProfilerBundle.Profile.quick':
            case 'XhprofProfilerBundle.Profile.summary':
            case 'XhprofProfilerBundle.Profile.flamegraph':
            case 'XhprofProfilerBundle.Profile.bottlenecksReport':
            case 'XhprofProfilerBundle.Profile.full':
            case 'XhprofProfilerBundle.Profile.callgraph':
            case 'XhprofProfilerBundle.Profile.callgraphNew':
            case 'XhprofProfilerBundle.Profile.compare':
            case 'XhprofProfilerBundle.Profile.callgraphCompare':
            case 'XhprofProfilerBundle.Profile.flamegraphCompare':
            case 'XhprofProfilerBundle.Profile.callsCompare':
            case 'XhprofProfilerBundle.Profile.relatedTraces':
            case 'XhprofProfilerBundle.Profile.logs':
                this.traceLoader.load(route.parameters)
                break

            case 'XhprofProfilerBundle.Profile.referencedTraces':
            case 'XhprofProfilerBundle.Profile.linkedTraces':
                this.traceLoader.load(route.parameters)
                this.profileListLoader.load(route.parameters)
                break

            case 'XhprofProfilerBundle.Profile.tracepoint':
                this.profileListLoader.loadForTracepoint(route.parameters)
                break

            case 'XhprofProfilerBundle.Profile.functionCompare':
                this.traceLoader.loadFunctionCompare(route.parameters)
                this.traceLoader.load(route.parameters)
                break

            case 'XhprofProfilerBundle.Profile.listCompare':
                this.profileListLoader.loadOperation(route.parameters)
                this.traceLoader.load(route.parameters)
                break

            case 'XhprofProfilerBundle.Snapshot.show':
                this.monitorLoader.loadBaseServiceHistoryRange(route.parameters)
                this.monitorLoader.loadGraph(route.parameters)
                this.snapshotLoader.load(route.parameters)

                this.eventLoader.loadGraph(route.parameters)
                break

            case 'XhprofOrganizationBundle.Application.show':
                this.monitorLoader.loadBaseServiceHistoryRange(route.parameters)
                this.monitorLoader.loadGraph(route.parameters)
                this.monitorLoader.loadCriticalOperations(route.parameters)

                this.eventLoader.loadList(route.parameters)
                this.eventLoader.loadEvent(route.parameters)
                this.eventLoader.loadGraph(route.parameters)
                break

            case 'XhprofProfilerBundle.CompareRange.show':
                this.eventLoader.loadCompareRange(route.parameters)
                break

            case 'XhprofOrganizationBundle.Application.trigger':
                this.traceLoader.loadToken(route.parameters)
                this.monitorLoader.loadMyProfiles(route.parameters)
                break

            case 'XhprofProfilerBundle.Profile.list':
                this.profileListLoader.load(route.parameters)
                break

            case 'XhprofErrorBundle.Issues.errors':
                this.errorLoader.loadList({ ...route.parameters, level: 'error' })
                this.errorLoader.loadError({ ...route.parameters, level: 'error' })
                break
            case 'XhprofErrorBundle.Issues.deprecations':
                this.errorLoader.loadList({ ...route.parameters, level: 'deprecated' })
                this.errorLoader.loadError({ ...route.parameters, level: 'deprecated' })
                break
            case 'XhprofErrorBundle.Issues.nonFatals':
                this.errorLoader.loadList({ ...route.parameters, level: 'non-fatals' })
                this.errorLoader.loadError({ ...route.parameters, level: 'non-fatals' })
                break
            case 'XhprofErrorBundle.Issues.slowSql':
                this.errorLoader.loadList({ ...route.parameters, level: 'slowsql' })
                this.errorLoader.loadError({ ...route.parameters, level: 'slowsql' })
                break
            case 'XhprofErrorBundle.Issues.observation':
                this.errorLoader.loadList({ ...route.parameters, level: 'observation' })
                this.errorLoader.loadError({ ...route.parameters, level: 'observation' })
                break
            case 'XhprofErrorBundle.Issues.release':
                this.errorLoader.loadList({ ...route.parameters, level: 'release' })

                if (route.parameters.error) {
                    this.eventLoader.loadEvent({ event: route.parameters.error })
                    this.eventLoader.loadGraph({ event: route.parameters.error })
                }

                break
            case 'XhprofErrorBundle.Issues.incident':
                this.errorLoader.loadList({ ...route.parameters, level: 'incident' })
                this.errorLoader.loadError({ ...route.parameters, level: 'incident' })

                if (route.parameters.error) {
                    this.eventLoader.loadGraphForIncident({ event: route.parameters.error })
                }

                break
        }
    }
}

export default Loader
